import TransparencyIcon from '@duyank/icons/external/Transparency';
import LockKeyIcon from '@duyank/icons/regular/LockKey';
import LockKeyOpenIcon from '@duyank/icons/regular/LockKeyOpen';
import ImageIcon from '@duyank/icons/regular/FileImage';
import React, { ChangeEvent, Fragment, useEffect, useMemo, useRef, useState } from 'react';
import Popover from '../common/popover/Popover';
import Slider from '../common/slider/Slider';
import { useEditor, useSelectedLayers } from '../hooks';
import { FrameLayerProps, ImageLayerProps, RootLayerProps } from '../layers';
import { Layer } from '../types/layer';
import { isFrameLayer, isGroupLayer, isImageLayer, isRootLayer, serializeLayers } from '../ultils/layer/layers';
import SettingButton from './SettingButton';
import LayerSidebar from './sidebar/LayerSidebar';
import { BoxSize, LayerComponentProps, SerializedLayerTree } from '@lidojs/design-core';
import { Menu, MenuItem, MenuButton } from '@szhsin/react-menu';
import { cloneDeep } from 'lodash';
import AppContext from '../../../AppContext';

const CommonSettings = () => {
  const transparencyButtonRef = useRef<HTMLDivElement>(null);
  const resizeButtonRef = useRef<HTMLDivElement>(null);
  const uploadRef = useRef<HTMLInputElement>(null);
  const widthRef = useRef<HTMLInputElement>(null);
  const heightRef = useRef<HTMLInputElement>(null);
  const copyRowRef = useRef<HTMLDivElement>(null);
  const copyColRef = useRef<HTMLDivElement>(null);
  const [openTransparencySetting, setOpenTransparencySetting] = useState(false);
  const [openResizeSetting, setOpenResizeSetting] = useState(false);
  const [lockSiteAspect, setLockSizeAspect] = useState(false);
  const { selectedLayers, selectedLayerIds } = useSelectedLayers();
  const { actions, activePage, sidebar, pageSize, isPageLocked, resizeOptions, page } = useEditor(
    (state, query) => ({
      activePage: state.activePage,
      sidebar: state.sidebar,
      pageSize: query.getPageSize(),
      isPageLocked:
        state.pages[state.activePage] &&
        state.pages[state.activePage].layers.ROOT.data.locked,
      resizeOptions: state.resizeOptions,
      page: state.pages[state.activePage]
    }),
  );
  const [size, setSize] = useState(pageSize);
  const [rows, setRows] = useState(1);
  const [cols, setCols] = useState(1);
  const [rowGap, setRowGap] = useState(5);
  const [colGap, setColGap] = useState(5);
  const [openCopyRows, setOpenCopyRows] = useState(false);
  const [openCopyCols, setOpenCopyCols] = useState(false);
  const {
    frameLayers,
  } = useMemo(() => {
    return selectedLayers.reduce(
      (acc, layer) => {
        if (layer.data.locked) {
          return acc;
        }
        if (isFrameLayer(layer)) {
          acc.frameLayers.push(layer);
        }
        return acc;
      },
      {
        frameLayers: [],
      } as {
        frameLayers: Layer<FrameLayerProps>[];
      }
    );
  }, [selectedLayers]);
  const {
    imageLayers,
  } = useMemo(() => {
    return selectedLayers.reduce(
      (acc, layer) => {
        if (layer.data.locked) {
          return acc;
        }
        if (isImageLayer(layer)) {
          acc.imageLayers.push(layer);
        }
        return acc;
      },
      {
        imageLayers: [],
      } as {
        imageLayers: Layer<ImageLayerProps>[];
      }
    );
  }, [selectedLayers]);
  useEffect(() => {
    setSize(pageSize);
  }, [pageSize]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const size = urlParams.get('size');
    if (resizeOptions) {
      switch (size) {
        case 'a4': handleResize(resizeOptions.a4.width, resizeOptions.a4.height);
        break;

        case 'us': handleResize(resizeOptions.usletter.width, resizeOptions.usletter.height);
        break;

        case 'a4l': handleResize(resizeOptions.a4landscape.width, resizeOptions.a4landscape.height);
        break;

        case 'usl': handleResize(resizeOptions.usletterlandscape.width, resizeOptions.usletterlandscape.height);
        break;

        case 'a7': handleResize(resizeOptions.a7card.width, resizeOptions.a7card.height);
        break;
      }
    }
  }, [resizeOptions])
  const { transparency } = useMemo(() => {
    return Object.entries(selectedLayers).reduce(
      (acc, [, layer]) => {
        if (isRootLayer(layer)) {
          acc.transparency = Math.max(
            acc.transparency,
            typeof layer.data.props.image?.transparency !== 'undefined'
              ? layer.data.props.image.transparency
              : 1
          );
        } else {
          acc.transparency = Math.max(
            acc.transparency,
            typeof layer.data.props.transparency !== 'undefined'
              ? layer.data.props.transparency
              : 1
          );
        }
        return acc;
      },
      { transparency: 0 }
    );
  }, [selectedLayers]);
  const isLocked = !!selectedLayers.find((l) => l.data.locked);
  const toggleLock = () => {
    if (isLocked) {
      actions.unlock(activePage, selectedLayerIds);
    } else {
      actions.lock(activePage, selectedLayerIds);
    }
  };
  const updateTransparency = (transparency: number) => {
    selectedLayerIds.forEach((layerId) => {
      if (layerId === 'ROOT') {
        actions.history
          .throttle(2000)
          .setProp<RootLayerProps>(activePage, layerId, {
            image: {
              transparency: transparency / 100,
            },
          });
      } else {
        actions.history.throttle(2000).setProp(activePage, layerId, {
          transparency: transparency / 100,
        });
      }
    });
  };
  const replaceImage = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = function () {
        const img = new Image;
        img.onload = function () {
          selectedLayers.forEach((layer) => {
            if (layer.data.type == 'Frame') {
              const boxSize: BoxSize = { width: img.width, height: img.height }
              actions.setImageData(img.src, layer.id, boxSize)
            }
          });
        };
        img.src = reader.result as string;
      };
      reader.readAsDataURL(file);
      e.target.value = '';
    }
  }
  const flipX = () => {
    selectedLayers.forEach((layer) => {
      if (isImageLayer(layer)) {
        actions.flipX(activePage, layer.id);
      }
    });
  }
  const flipY = () => {
    selectedLayers.forEach((layer) => {
      if (isImageLayer(layer)) {
        actions.flipY(activePage, layer.id);
      }
    });
  }
  const handleCount = (value: number) => {
    if (value <= 0) value = 1;
    return Math.min(value, 20);
  }
  const copyRows = async () => {
    const data: SerializedLayerTree[] = [];
    selectedLayerIds.forEach((layerId) => {
      data.push({
        rootId: layerId,
        layers: cloneDeep(
          serializeLayers(page.layers, layerId)
        ),
      });
    });
    let posx, posy, boxsize: BoxSize;
    for (let index = 1; index <= rows; index++) {
      actions.addLayerTrees(
        data.map((serializedLayers) => {
          Object.entries(serializedLayers.layers).forEach(([layerId]) => {
            posx = (serializedLayers.layers[layerId].props as LayerComponentProps).position.x;
            posy = (serializedLayers.layers[layerId].props as LayerComponentProps).position.y;
            boxsize = (serializedLayers.layers[layerId].props as LayerComponentProps).boxSize;
          });
          return serializedLayers;
        }),
        posx, posy, 0, index * (boxsize.height + rowGap)
      );
    }
  }
  const copyCols = async () => {
    const data: SerializedLayerTree[] = [];
    selectedLayerIds.forEach((layerId) => {
      data.push({
        rootId: layerId,
        layers: cloneDeep(
          serializeLayers(page.layers, layerId)
        ),
      });
    });
    let posx, posy, boxsize: BoxSize;
    for (let index = 1; index <= cols; index++) {
      actions.addLayerTrees(
        data.map((serializedLayers) => {
          Object.entries(serializedLayers.layers).forEach(([layerId]) => {
            posx = (serializedLayers.layers[layerId].props as LayerComponentProps).position.x;
            posy = (serializedLayers.layers[layerId].props as LayerComponentProps).position.y;
            boxsize = (serializedLayers.layers[layerId].props as LayerComponentProps).boxSize;
          });
          return serializedLayers;
        }),
        posx, posy, index * (boxsize.width + colGap), 0
      );
    }
  }
  useEffect(() => {
    setOpenTransparencySetting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(selectedLayerIds)]);

  const handleChangeSize = (value: string, type: 'width' | 'height') => {
    const ratio = size.width / size.height;
    const v = parseInt(value, 10);
    if (type === 'width') {
      if (lockSiteAspect) {
        (heightRef.current as HTMLInputElement).value = String(
          Math.round((v / ratio) * 10) / 10
        );
      }
      setSize({ ...size, width: v });
    }
    if (type === 'height') {
      if (lockSiteAspect) {
        (widthRef.current as HTMLInputElement).value = String(
          Math.round(v * ratio * 10) / 10
        );
      }
      setSize({ ...size, height: v });
    }
  };

  const isDisabledResize = useMemo(
    () => size.width < 100 || size.height < 100,
    [size]
  );

  const handleResize = (width: number, height: number) => {
    // if (isDisabledResize) return;
    const boxSize: BoxSize = { width: width, height: height }
    actions.changePageSize(boxSize);
    setOpenResizeSetting(false);
  };
  return (
    <Fragment>
      <div
        css={{
          display: 'grid',
          alignItems: 'center',
          gridAutoFlow: 'column',
          gridGap: 8,
        }}
      >
        <SettingButton onClick={() => actions.setSidebar('LAYER_MANAGEMENT')}>
          <span css={{ padding: '0 4px' }}>Position</span>
        </SettingButton>

        {selectedLayerIds.length > 0 &&
          !isLocked &&
          !isPageLocked &&
          (!isRootLayer(selectedLayers[0]) ||
            (isRootLayer(selectedLayers[0]) &&
              selectedLayers[0].data.props.image)) && (
            <Fragment>
              <div
                css={{
                  height: 24,
                  width: `1px`,
                  background: 'rgba(57,76,96,.15)',
                }}
              />
              <SettingButton
                ref={transparencyButtonRef}
                css={{ fontSize: 20 }}
                onClick={() => setOpenTransparencySetting(true)}
              >
                <TransparencyIcon />
              </SettingButton>
              <Popover
                anchorEl={transparencyButtonRef.current}
                offsets={{
                  'bottom-end': { x: 0, y: 8 },
                }}
                open={openTransparencySetting}
                placement={'bottom-end'}
                onClose={() => setOpenTransparencySetting(false)}
              >
                <div css={{ padding: 16 }}>
                  <Slider
                    defaultValue={transparency * 100}
                    label={'Transparency'}
                    onChange={updateTransparency}
                  />
                </div>
              </Popover>
            </Fragment>
          )}
        {!isPageLocked && (
          <Fragment>
            <div
              css={{
                height: 24,
                width: `1px`,
                background: 'rgba(57,76,96,.15)',
              }}
            />
            {resizeOptions && (
              <div>
                <Menu
                  menuButton={<MenuButton
                    css={{
                      background: !isDisabledResize ? '#ffffff' : 'rgba(57,76,96,.15)',
                      height: 32,
                      padding: '4px 8px',
                      borderRadius: 4,
                      cursor: !isDisabledResize ? 'pointer' : 'not-allowed',
                      textAlign: 'center',
                      ':hover': {
                        background: !isDisabledResize ? '#f6f6f6' : undefined,
                      }
                    }}
                  >Resize</MenuButton>}>
                  {resizeOptions.a4 && (
                    <MenuItem css={{ padding: 10, color: '#5E6278' }} onClick={() => handleResize(resizeOptions.a4.width, resizeOptions.a4.height)}>{resizeOptions.a4.label}</MenuItem>
                  )}
                  {resizeOptions.usletter && (
                    <MenuItem css={{ padding: 10, color: '#5E6278' }} onClick={() => handleResize(resizeOptions.usletter.width, resizeOptions.usletter.height)}>{resizeOptions.usletter.label}</MenuItem>
                  )}
                  {resizeOptions.a4landscape && (
                    <MenuItem css={{ padding: 10, color: '#5E6278' }} onClick={() => handleResize(resizeOptions.a4landscape.width, resizeOptions.a4landscape.height)}>{resizeOptions.a4landscape.label}</MenuItem>
                  )}
                  {resizeOptions.usletterlandscape && (
                    <MenuItem css={{ padding: 10, color: '#5E6278' }} onClick={() => handleResize(resizeOptions.usletterlandscape.width, resizeOptions.usletterlandscape.height)}>{resizeOptions.usletterlandscape.label}</MenuItem>
                  )}
                  {resizeOptions.a7card && (
                    <MenuItem css={{ padding: 10, color: '#5E6278' }} onClick={() => handleResize(resizeOptions.a7card.width, resizeOptions.a7card.height)}>{resizeOptions.a7card.label}</MenuItem>
                  )}

                </Menu>
              </div>
            )}
          </Fragment>
        )}
        {selectedLayerIds.length > 0 && (
          <SettingButton
            css={{ fontSize: 20 }}
            isActive={isLocked}
            onClick={toggleLock}
          >
            {isLocked && <LockKeyIcon />}
            {!isLocked && <LockKeyOpenIcon />}
          </SettingButton>
        )}
      </div>
      {sidebar === 'LAYER_MANAGEMENT' && <LayerSidebar open={true} />}
      {frameLayers.length > 0 && !isPageLocked && (
        <div>
          <div
            css={{
              marginRight: '8px',
              float: 'left',
              height: 24,
              width: `1px`,
              background: 'rgba(57,76,96,.15)',
            }}
          />
          <SettingButton
            onClick={() => uploadRef.current?.click()}
          >
            <input
              ref={uploadRef}
              accept="image/*"
              css={{ display: 'none' }}
              type="file"
              onChange={replaceImage}
            />
            <ImageIcon css={{ zoom: 1.2, margin: '3px' }} /><span css={{ padding: '4px 4px' }}> Replace Image</span>
          </SettingButton>
        </div>
      )}
      {imageLayers.length > 0 && (
        <>
          <div>
            <SettingButton
              onClick={() => flipX()}
            >
              <span css={{ padding: '4px 4px' }}> FlipH</span>
            </SettingButton>
          </div>
          <div>
            <SettingButton
              onClick={() => flipY()}
            >
              <span css={{ padding: '4px 4px' }}> FlipV</span>
            </SettingButton>
          </div>
        </>
      )}
      {AppContext.isAdmin && (
        <>
          <div>
            <SettingButton
              ref={copyRowRef}
              onClick={() => setOpenCopyRows(true)}
            >
              <span css={{ padding: '4px 4px' }}> Copy Rows</span>
            </SettingButton>
            <Popover
              anchorEl={copyRowRef.current}
              offsets={{
                'bottom-end': { x: 0, y: 8 },
              }}
              open={openCopyRows}
              placement={'bottom'}
              onClose={() => setOpenCopyRows(false)}
            >
              <div css={{
                width: 'max-content',
                padding: 10,
                display: 'flex',
                flexDirection: 'row'
              }}>
                <div css={{
                  display: 'flex',
                  gap: 6,
                  padding: 6,
                  flexDirection: 'column'
                }}>
                  <div css={{
                    padding: '2px',
                  }}>Count</div>
                  <input type="number" min="1" max="20" css={{
                    border: '1px solid #d9d9d9',
                    display: 'inline-block',
                    padding: 5,
                    width: 50
                  }} value={rows}
                    onChange={(e) => setRows(handleCount(parseInt(e.target.value)))}
                  />
                </div>

                <div css={{
                  display: 'flex',
                  gap: 6,
                  padding: 6,
                  flexDirection: 'column'
                }}>
                  <div css={{
                    padding: '2px',
                  }}>Gap</div>
                  <input type="number" min="0" max="200" css={{
                    border: '1px solid #d9d9d9',
                    display: 'inline-block',
                    padding: 5,
                    width: 50
                  }} value={rowGap}
                    onChange={(e) => setRowGap(parseInt(e.target.value))}
                  />
                </div>
              </div>
              <SettingButton css={{
                background: '#d6d6d6',
                borderRadius: 0
              }}
                onClick={copyRows}
              >Submit</SettingButton>
            </Popover>
          </div>
          <div>
            <SettingButton
              ref={copyColRef}
              onClick={() => setOpenCopyCols(true)}
            >
              <span css={{ padding: '4px 4px' }}> Copy Columns</span>
            </SettingButton>
            <Popover
              anchorEl={copyColRef.current}
              offsets={{
                'bottom-end': { x: 0, y: 8 },
              }}
              open={openCopyCols}
              placement={'bottom'}
              onClose={() => setOpenCopyCols(false)}
            >
              <div css={{
                width: 'max-content',
                padding: 10,
                display: 'flex',
                flexDirection: 'row'
              }}>
                <div css={{
                  display: 'flex',
                  gap: 6,
                  padding: 6,
                  flexDirection: 'column'
                }}>
                  <div css={{
                    padding: '2px',
                  }}>Count</div>
                  <input type="number"
                    min="1" max="20"
                    css={{
                      border: '1px solid #d9d9d9',
                      display: 'inline-block',
                      padding: 5,
                      width: 50
                    }} value={cols}
                    onChange={(e) => setCols(handleCount(parseInt(e.target.value)))}
                  />
                </div>

                <div css={{
                  display: 'flex',
                  gap: 6,
                  padding: 6,
                  flexDirection: 'column'
                }}>
                  <div css={{
                    padding: '2px',
                  }}>Gap</div>
                  <input type="number"
                    min="0" max="200"
                    css={{
                      border: '1px solid #d9d9d9',
                      display: 'inline-block',
                      padding: 5,
                      width: 50
                    }} value={colGap}
                    onChange={(e) => setColGap(Math.min(parseInt(e.target.value), 200))}

                  />
                </div>
              </div>
              <SettingButton css={{
                background: '#d6d6d6',
                borderRadius: 0
              }}
                onClick={copyCols}
              >Submit</SettingButton>
            </Popover>
          </div>
        </>
      )}
    </Fragment>
  );
};

export default CommonSettings;
